import { useState, useEffect, useCallback } from 'react';
import { apiService } from '../api/api.service';
import { ProcessingStatus } from '../components/shared/ProcessingStatusIndicator';

/**
 * Хук для отслеживания статуса обработки видео
 * 
 * Логика работы:
 * 1. Если response.status === 'ready' - видео готово, прекращаем обновления
 * 2. Если response.task_status.status === 'no_task' - задачи нет, считаем завершенным
 * 3. Автоматическое обновление работает только для видео в статусе 'processing'
 * 4. Для готовых видео (status: 'ready') API не пингуется
 */

interface VideoProcessingStatus {
    video_id: number;
    status: ProcessingStatus;
    title: string;
    url: string | null;
    preview_url: string | null;
    created_at: string;
    task_status: {
        status: string;
        message: string;
    };
}

interface UseVideoProcessingStatusOptions {
    videoId: number;
    autoRefresh?: boolean;
    refreshInterval?: number;
    onStatusChange?: (status: ProcessingStatus, data?: VideoProcessingStatus) => void;
}

export const useVideoProcessingStatus = ({
    videoId,
    autoRefresh = true,
    refreshInterval = 5000,
    onStatusChange
}: UseVideoProcessingStatusOptions) => {
    const [status, setStatus] = useState<ProcessingStatus>('processing');
    const [message, setMessage] = useState<string>('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [lastData, setLastData] = useState<VideoProcessingStatus | null>(null);

    const fetchStatus = useCallback(async () => {
        if (!videoId) return;

        try {
            setIsLoading(true);
            setError(null);

            const response = await apiService.getVideoProcessingStatus(videoId);

            // Проверяем основной статус видео
            if (response.status === 'ready') {
                // Если видео готово, устанавливаем статус completed и прекращаем обновления
                setLastData(response);
                setStatus('completed');
                setMessage('Видео готово к просмотру');

                // Вызываем callback при изменении статуса
                if (onStatusChange) {
                    onStatusChange('completed', response);
                }
                return; // Прерываем выполнение, чтобы не устанавливать isLoading = false
            }

            // Преобразуем статус задачи в наш ProcessingStatus
            let processingStatus: ProcessingStatus = 'processing';
            switch (response.task_status.status) {
                case 'completed':
                    processingStatus = 'completed';
                    break;
                case 'failed':
                    processingStatus = 'failed';
                    break;
                case 'error':
                    processingStatus = 'error';
                    break;
                case 'no_task':
                    // Если задачи нет, но видео готово, считаем его завершенным
                    processingStatus = 'completed';
                    break;
                default:
                    processingStatus = 'processing';
            }

            console.log(`Video ${videoId} task status: ${processingStatus} - ${response.task_status.message}`);

            // Сохраняем полные данные ответа
            setLastData(response);
            setStatus(processingStatus);
            setMessage(response.task_status.message);

            // Вызываем callback при изменении статуса
            if (onStatusChange) {
                onStatusChange(processingStatus, response);
            }

        } catch (err) {
            console.error('Error fetching video processing status:', err);
            setError('Ошибка получения статуса');
            setStatus('error');
            setMessage('Не удалось получить статус');
        } finally {
            setIsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [videoId]);

    useEffect(() => {
        fetchStatus();

        // Автоматически обновляем только если:
        // 1. Включено автообновление
        // 2. Статус все еще processing (видео не готово)
        // 3. Есть videoId
        if (autoRefresh && videoId && status === 'processing') {
            console.log(`Starting auto-refresh for video ${videoId} every ${refreshInterval}ms`);
            const interval = setInterval(fetchStatus, refreshInterval);
            return () => {
                console.log(`Stopping auto-refresh for video ${videoId}`);
                clearInterval(interval);
            };
        }
    }, [fetchStatus, autoRefresh, refreshInterval, status, videoId]);

    const refreshStatus = useCallback(() => {
        fetchStatus();
    }, [fetchStatus]);

    return {
        status,
        message,
        isLoading,
        error,
        refreshStatus,
        lastData
    };
};
